/**
 * 应付对账工作台 - 统一批次上下文
 *
 * 职责：作为整个工作台的唯一数据中心。
 *
 * 重构: Pipeline-aware — 所有状态分类、进度、操作通过 pipeline.js 派生。
 *
 * 数据来源:
 *   1. IDS 列表 — GET /settlement/ap/workbench/ids-list
 *   2. 批次汇总 V2 — POST /settlement/ap/workbench/batch-summary-v2
 *   3. Pipeline 操作 — POST /settlement/ap/workbench/pipeline/*
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import workbenchApi from "../services/workbenchApi";
import {
    getDisplayStatus,
    parseProgress,
    inferRetryStage,
    TOTAL_STAGES,
    checkBatchValidationReadiness,
} from "../pipeline";

const BatchContext = createContext(null);

// 到达这些 display status 的 IDS 可以参与矩阵计算；VOIDED 作废数据不参与矩阵。
const MATRIX_ELIGIBLE_DISPLAY_STATUSES = ["REVIEW", "CONFIRMED", "REJECTED"];
// 附加：ALLOCATION_COMPLETED 虽然 displayStatus=PROCESSING 但也有费用数据；VOIDED 与后端 MATRIX_ELIGIBLE_STATUSES 保持排除。
const MATRIX_ELIGIBLE_BACKEND_STATUSES = [
    "ALLOCATION_COMPLETED",
    "VALIDATION_IN_PROGRESS",
    "VALIDATION_COMPLETED",
    "BATCH_VALIDATION_IN_PROGRESS",
    "BATCH_VALIDATION_COMPLETED",
    "BATCH_VALIDATION_FAILED",
    "CONFIRMED",
    "REJECTED",
];

const BUSINESS_ERROR_STATUSES = ["HAS_ERROR", "HAS_ERRORS", "ERROR", "MISMATCH", "PARTIAL", "HAS_ISSUES"];

const collectValidationExecutionFailures = (crossValidation) => {
    if (!crossValidation) return [];
    const explicitFailures = Array.isArray(crossValidation.execution_failures) ? crossValidation.execution_failures : [];
    const voteFailures = Array.isArray(crossValidation.votes)
        ? crossValidation.votes
            .filter((vote) => vote?.execution_failed)
            .map((vote) => ({
                task_id: vote.task_id,
                candidate_index: vote.candidate_index,
                execution_id: vote.execution_id,
                terminal_error_type: vote.terminal_error_type,
                failure_reason: vote.failure_reason,
                execution_evidence: vote.execution_evidence,
            }))
        : [];
    const seen = new Set();
    return [...explicitFailures, ...voteFailures].filter((failure) => {
        const key = [failure?.task_id, failure?.execution_id, failure?.candidate_index].join(":");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const hasValidationExecutionFailure = (crossValidation) => {
    if (!crossValidation) return false;
    if (crossValidation.has_execution_failure || Number(crossValidation.execution_failure_count || 0) > 0) return true;
    if (collectValidationExecutionFailures(crossValidation).length > 0) return true;
    return Object.values(crossValidation.products_matrix || {}).some((item) =>
        (item?.agent_results || []).some((result) => result?.execution_failed),
    );
};

const hasBusinessValidationIssue = (crossValidation) => {
    if (!crossValidation) return false;

    const productHasBusinessIssue = Object.values(crossValidation.products_matrix || {}).some((item) =>
        (item?.agent_results || []).some((result) => !result?.execution_failed && result?.match === false),
    );
    const voteHasBusinessIssue = (crossValidation.votes || []).some((vote) =>
        !vote?.execution_failed && String(vote?.status || "").toUpperCase() !== "PASSED",
    );
    const errorCount = Number(crossValidation.error_count || 0);
    const executionFailureCount = Number(crossValidation.execution_failure_count || 0);
    const status = String(crossValidation.status || "").toUpperCase();

    return productHasBusinessIssue
        || voteHasBusinessIssue
        || errorCount > executionFailureCount
        || (BUSINESS_ERROR_STATUSES.includes(status) && !hasValidationExecutionFailure(crossValidation));
};

export function BatchProvider({ children }) {
    const [searchParams] = useSearchParams();
    const batchNumber = searchParams.get("batch");
    const vendorId = searchParams.get("vendor");
    const standardCostType = searchParams.get("costType");

    // ============================================================
    // 第一层：IDS 列表 + 基础信息
    // ============================================================
    const [idsList, setIdsList] = useState([]);
    const [idsLoading, setIdsLoading] = useState(false);
    const [idsError, setIdsError] = useState(null);
    const idsListRef = useRef(idsList); // 用于 diff 检测，避免无变化时的级联更新
    idsListRef.current = idsList;

    // 从 IDS 列表聚合的批次基础信息
    const batchInfo = useMemo(() => {
        if (idsList.length === 0) {
            return {
                vendorId: null, vendorName: null, standardCostType: null,
                airportCode: null, currency: null,
            };
        }
        const first = idsList[0];
        return {
            vendorId: first.vendor_id,
            vendorName: first.vendor_name,
            standardCostType: first.standard_cost_type,
            airportCode: first.airport_code,
            currency: first.currency,
        };
    }, [idsList]);

    // ============================================================
    // Pipeline-aware 状态统计
    // ============================================================
    const statusSummary = useMemo(() => {
        const result = { total: idsList.length, pending: 0, processing: 0, failed: 0, blocked: 0, review: 0, confirmed: 0, rejected: 0, voided: 0, completed: 0 };
        idsList.forEach((item) => {
            const ds = getDisplayStatus(item.status);
            switch (ds) {
                case "PENDING": result.pending++; break;
                case "PROCESSING": result.processing++; break;
                case "FAILED": result.failed++; break;
                case "BLOCKED": result.blocked++; break;
                case "REVIEW": result.review++; break;
                case "CONFIRMED": result.confirmed++; result.completed++; break;
                case "REJECTED": result.rejected++; result.completed++; break;
                case "VOIDED": result.voided++; result.completed++; break;
            }
        });
        return result;
    }, [idsList]);

    const applyIdsStatusUpdate = useCallback((idsId, newStatus) => {
        if (!idsId || !newStatus) return;
        setIdsList((prev) =>
            prev.map((item) =>
                String(item.id) === String(idsId)
                    ? { ...item, status: newStatus }
                    : item,
            ),
        );
    }, []);

    // 整批核查就绪状态（前端防呆用）
    const batchValidationReadiness = useMemo(
        () => checkBatchValidationReadiness(idsList),
        [idsList],
    );

    // Pipeline 进度总览
    const pipelineOverview = useMemo(() => {
        if (idsList.length === 0) return null;

        let totalCompleted = 0;
        const totalPossible = idsList.length * TOTAL_STAGES;

        idsList.forEach((item) => {
            const { completedStages } = parseProgress(item.status);
            totalCompleted += completedStages;
        });

        return {
            overallPercent: totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0,
            totalIds: idsList.length,
            totalStages: TOTAL_STAGES,
        };
    }, [idsList]);

    // ============================================================
    // 加载 IDS 列表
    // ============================================================
    /**
     * 加载 IDS 列表
     *
     * @param {Object} options
     * @param {boolean} options.silent - 静默刷新：不显示 loading 状态，不清空 error
     *   用于轮询场景，避免每 N 秒表格闪 loading spinner
     */
    const loadIdsList = useCallback(async ({ silent = false } = {}) => {
        if (!batchNumber || !vendorId || !standardCostType) return;
        if (!silent) {
            setIdsLoading(true);
            setIdsError(null);
        }
        try {
            const result = await workbenchApi.getIdsList(
                batchNumber,
                vendorId,
                standardCostType,
            );
            const items = result?.items || [];

            // Diff 检测：只有行级可见状态实际变化时才更新 state，
            // 避免创建新数组引用导致 eligibleIds → loadMatrixData 级联重载
            const prev = idsListRef.current;
            const hasChanged =
                items.length !== prev.length ||
                items.some((item, i) => (
                    item.status !== prev[i]?.status ||
                    item.processing_issue_reason !== prev[i]?.processing_issue_reason ||
                    JSON.stringify(item.ocr_options || null) !== JSON.stringify(prev[i]?.ocr_options || null) ||
                    JSON.stringify(item.invoice_summary || null) !== JSON.stringify(prev[i]?.invoice_summary || null)
                ));

            if (hasChanged) {
                setIdsList(items);
            }
        } catch (err) {
            if (!silent) {
                console.error("[BatchContext] 加载 IDS 列表失败:", err);
                setIdsError(err.message || "加载失败");
                message.error("加载批次数据失败");
            }
        } finally {
            if (!silent) {
                setIdsLoading(false);
            }
        }
    }, [batchNumber, vendorId, standardCostType]);

    // ============================================================
    // 第二层：矩阵数据 + 验证结果
    // ============================================================
    const [matrixData, setMatrixData] = useState(null);
    const [matrixLoading, setMatrixLoading] = useState(false);
    const [matrixError, setMatrixError] = useState(null);
    const [excludeSources, setExcludeSources] = useState([]);
    const excludeSourcesRef = useRef(excludeSources);
    excludeSourcesRef.current = excludeSources;

    const eligibleIds = useMemo(() => {
        return idsList
            .filter((item) => MATRIX_ELIGIBLE_BACKEND_STATUSES.includes(item.status))
            .map((item) => String(item.id));
    }, [idsList]);

    const matrixScopeSignature = useMemo(
        () => idsList.map((item) => `${item.id}:${item.status}`).join("|"),
        [idsList],
    );

    const loadMatrixData = useCallback(async (overrideExcludes) => {
        if (!batchNumber || !vendorId || !standardCostType) {
            setMatrixData(null);
            return;
        }
        if (idsList.length === 0 || eligibleIds.length === 0) {
            setMatrixData(null);
            return;
        }
        setMatrixLoading(true);
        setMatrixError(null);
        const currentExcludes = overrideExcludes !== undefined ? overrideExcludes : excludeSourcesRef.current;
        try {
            const result = await workbenchApi.getBatchSummaryV2(
                batchNumber,
                vendorId,
                standardCostType,
                currentExcludes.length > 0 ? currentExcludes : null,
            );
            if (result.success) {
                setMatrixData(result.data);
            } else {
                setMatrixError(result.error || "获取对账数据失败");
            }
        } catch (err) {
            setMatrixError(err.message || "网络错误");
        } finally {
            setMatrixLoading(false);
        }
    }, [batchNumber, vendorId, standardCostType, idsList.length, eligibleIds.length]);

    const toggleSource = useCallback((sourceKey, enabled) => {
        setExcludeSources((prev) => {
            const next = enabled
                ? prev.filter((k) => k !== sourceKey)
                : [...prev, sourceKey];
            setTimeout(() => loadMatrixData(next), 0);
            return next;
        });
    }, [loadMatrixData]);

    // ============================================================
    // 验证结果合并
    // ============================================================
    const validationMap = useMemo(() => {
        const map = {};

        // 来源 1：整批核查结果（ApInvoiceBatchValidationAgent）
        // 后端 _load_validation_results 展开后，flight_results 直接在 vr 顶层
        const vr = matrixData?.validation_results;
        if (Array.isArray(vr?.flight_results)) {
            vr.flight_results.forEach((fr) => {
                map[fr.flight_id] = {
                    ...fr,
                    batchValidation: fr,
                    businessValidationStatus: fr.status,
                };
            });
        }

        // 来源 2：交叉验证结果（FlightCostValidatorAgent 投票汇总）
        const cvm = matrixData?.cross_validation_map;
        if (cvm) {
            Object.entries(cvm).forEach(([flightIdStr, cvData]) => {
                const flightId = parseInt(flightIdStr, 10);
                if (isNaN(flightId)) return;
                if (!map[flightId]) map[flightId] = {};
                map[flightId].crossValidation = cvData;

                const executionFailed = hasValidationExecutionFailure(cvData);
                const businessHasError = hasBusinessValidationIssue(cvData);
                const existingBusinessStatus = map[flightId].businessValidationStatus || map[flightId].status;
                const existingBusinessHasError = BUSINESS_ERROR_STATUSES.includes(String(existingBusinessStatus || "").toUpperCase());

                map[flightId].executionFailed = executionFailed;
                map[flightId].executionFailures = collectValidationExecutionFailures(cvData);
                map[flightId].businessValidationStatus = businessHasError || existingBusinessHasError
                    ? "HAS_ISSUES"
                    : (existingBusinessStatus || "PASSED");

                if (executionFailed) {
                    map[flightId].status = "EXECUTION_FAILED";
                } else if (map[flightId].businessValidationStatus === "HAS_ISSUES") {
                    map[flightId].status = "HAS_ISSUES";
                } else if (!map[flightId].status) {
                    map[flightId].status = "PASSED";
                }
            });
        }

        return map;
    }, [matrixData?.validation_results, matrixData?.cross_validation_map]);

    const validationSummary = useMemo(() => {
        const vr = matrixData?.validation_results;
        const cvm = matrixData?.cross_validation_map;
        if (!vr && !cvm) return null;

        const allFlightIds = Object.keys(validationMap);
        let passed = 0, hasIssues = 0, withWarnings = 0, executionFailedFlights = 0;
        const failedValidationTaskIds = new Set();
        allFlightIds.forEach((fid) => {
            const fv = validationMap[fid];
            if (fv.executionFailed || fv.status === "EXECUTION_FAILED") executionFailedFlights++;

            const businessStatus = fv.businessValidationStatus || (fv.status === "EXECUTION_FAILED" ? null : fv.status);
            if (businessStatus === "HAS_ISSUES") hasIssues++;
            else if (businessStatus === "PASSED_WITH_WARNINGS") withWarnings++;
            else if (businessStatus === "PASSED") passed++;

            const votes = fv?.crossValidation?.votes || [];
            votes.forEach((vote) => {
                if (vote?.execution_failed && vote?.task_id != null) {
                    failedValidationTaskIds.add(String(vote.task_id));
                }
            });
        });

        // 后端展开后，summary 直接在 vr 顶层
        const batchSummary = vr?.summary || {};
        return {
            status: hasIssues > 0 ? "HAS_ERRORS" : (executionFailedFlights > 0 ? "EXECUTION_FAILED" : (withWarnings > 0 ? "WITH_WARNINGS" : "PASSED")),
            validatedAt: vr?.validated_at,
            totalFlights: allFlightIds.length,
            passed,
            withWarnings,
            hasIssues,
            chargesPassed: batchSummary.charges_passed || 0,
            chargesFailed: batchSummary.charges_failed || 0,
            chargesNoRule: batchSummary.charges_no_rule || 0,
            taskSummary: vr?.task_summary,
            taskFeedback: vr?.task_feedback,
            failedValidationTaskCount: failedValidationTaskIds.size,
            executionFailedFlights,
            hasCrossValidation: !!cvm,
            hasBatchValidation: !!vr,
        };
    }, [matrixData?.validation_results, matrixData?.cross_validation_map, validationMap]);

    // ============================================================
    // Pipeline 操作
    // ============================================================

    /**
     * 启动单个 IDS 的 Pipeline
     */
    const startPipeline = useCallback(async (idsId, extraPrompt) => {
        try {
            await workbenchApi.startPipeline(idsId, extraPrompt);
            message.success("已启动处理");
            await loadIdsList();
        } catch (err) {
            message.error(`启动失败: ${err.message}`);
        }
    }, [loadIdsList]);

    /**
     * 从指定阶段重试
     */
    const retryFromStage = useCallback(async (idsId, stageId, extraPrompt, highResolutionPdfOcr, pdfRenderDpi) => {
        try {
            await workbenchApi.retryFromStage(idsId, stageId, extraPrompt, highResolutionPdfOcr, pdfRenderDpi);
            message.success(`已从${stageId}重新开始`);
            await loadIdsList();
        } catch (err) {
            message.error(`重试失败: ${err.message}`);
        }
    }, [loadIdsList]);

    /**
     * 智能重试（根据当前状态推断阶段）
     */
    const smartRetry = useCallback(async (idsId, status) => {
        const stageId = inferRetryStage(status);
        await retryFromStage(idsId, stageId);
    }, [retryFromStage]);

    /**
     * 批量 Pipeline 操作
     */
    const batchPipelineAction = useCallback(async (idsIds, action, stageId, options = {}) => {
        try {
            const result = await workbenchApi.batchPipelineAction(idsIds, action, stageId);
            const payload = result?.data || {};
            const successCount = result?.success_count ?? payload.succeeded ?? 0;
            const failCount = result?.fail_count ?? payload.failed ?? 0;
            const normalized = {
                successCount,
                failCount,
                errors: payload.errors || result?.errors || [],
                success: result?.success ?? failCount === 0,
            };

            if (!options.silent) {
                message.info(`批量操作完成: ${successCount} 成功, ${failCount} 失败`);
            }
            await loadIdsList();
            return normalized;
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "批量操作失败";
            if (!options.silent) {
                message.error(`批量操作失败: ${detail}`);
            }
            return {
                successCount: 0,
                failCount: idsIds.length,
                errors: [{ error: detail }],
                success: false,
            };
        }
    }, [loadIdsList]);

    /**
     * 停止处理
     */
    const stopPipeline = useCallback(async (idsId) => {
        try {
            await workbenchApi.stopPipeline(idsId);
            message.success("已停止处理");
            await loadIdsList();
        } catch (err) {
            message.error(`停止失败: ${err.message}`);
        }
    }, [loadIdsList]);

    /**
     * 触发整批核查
     *
     * 前端防呆 + 后端校验双保险。前端先用 checkBatchValidationReadiness 检查，
     * 后端的 /pipeline/batch-validation 端点会再做一次完整校验。
     */
    const triggerBatchValidation = useCallback(async () => {
        if (!batchNumber || !vendorId || !standardCostType) {
            message.error("缺少批次信息");
            return;
        }

        // 前端防呆检查
        const readiness = checkBatchValidationReadiness(idsList);
        if (!readiness.ready) {
            if (readiness.total === 0) {
                message.warning("批次内没有需要验证的 IDS");
                return;
            }
            const names = readiness.ineligible.slice(0, 3).map((i) => i.filename).join("、");
            message.warning(
                `批次内有 ${readiness.ineligible.length} 张账单尚未完成验证航班，` +
                `无法触发整批核查（${names}${readiness.ineligible.length > 3 ? "等" : ""}）`,
            );
            return;
        }

        try {
            const result = await workbenchApi.triggerBatchValidation(
                batchNumber,
                vendorId,
                standardCostType,
            );
            message.success(result.message || "整批核查已触发");
            await loadIdsList();
        } catch (err) {
            // 后端返回的校验错误信息直接展示
            const detail = err?.response?.data?.detail || err.message || "触发整批核查失败";
            message.error(detail);
        }
    }, [batchNumber, vendorId, standardCostType, idsList, loadIdsList]);

    // ============================================================
    // 生命周期
    // ============================================================
    useEffect(() => {
        loadIdsList();
    }, [loadIdsList]);

    useEffect(() => {
        if (idsList.length > 0 && eligibleIds.length > 0) {
            loadMatrixData();
        } else {
            setMatrixData(null);
        }
    }, [idsList.length, eligibleIds.length, matrixScopeSignature, loadMatrixData]);

    // ============================================================
    // 便捷刷新
    // ============================================================

    /** 静默刷新 IDS 列表（轮询专用：无 loading、有 diff 检测） */
    const pollRefresh = useCallback(async () => {
        await loadIdsList({ silent: true });
    }, [loadIdsList]);

    /** 显式刷新（关闭抽屉、用户点按钮等场景：刷新 IDS 列表 + 矩阵数据） */
    const refreshBatch = useCallback(async () => {
        await loadIdsList();
        // 用户在抽屉中修改航班关联后 IDS status 不变（仍是 REVIEW），eligibleIds 不会变化，
        // 矩阵 useEffect 不会触发。因此必须显式刷新矩阵数据以反映航班修改。
        if (eligibleIds.length > 0) {
            await loadMatrixData();
        }
    }, [loadIdsList, eligibleIds, loadMatrixData]);

    const refreshMatrix = useCallback(async () => {
        await loadMatrixData();
    }, [loadMatrixData]);

    const refreshAll = useCallback(async () => {
        await loadIdsList();
        if (eligibleIds.length > 0) {
            await loadMatrixData();
        }
    }, [loadIdsList, eligibleIds, loadMatrixData]);

    const deleteInvoiceDataSources = useCallback(async (idsIds) => {
        const result = await workbenchApi.batchDeleteInvoiceDataSources(idsIds);
        await refreshAll();
        return result?.data || { succeeded: 0, failed: idsIds.length, results: [] };
    }, [refreshAll]);

    const batchSetSettlementCurrency = useCallback(async (idsIds, currency) => {
        try {
            const result = await workbenchApi.batchSetSettlementCurrency(idsIds, currency);
            if (result.success) {
                message.success(result.message || `已设置 ${result.data?.succeeded || 0} 个账单的结算币种`);
            } else {
                message.warning(result.message || "部分账单设置失败");
            }
            await refreshAll();
            return result;
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "批量设置结算币种失败";
            message.error(detail);
            throw err;
        }
    }, [refreshAll]);

    // ============================================================
    // 账单标识查找表（Invoice Identity Map）
    //
    // 设计原理：
    //   后端 projection_service 返回 invoices[] 包含每张账单的完整标识
    //   (invoice_data_source_id, invoice_number)。此 Map 在 Context 层
    //   统一构建，供所有子组件消费，确保账单标识的展示策略全局一致。
    //   short_label 采用最小区分前缀算法，保证同批次账单号截断后仍可区分。
    // ============================================================
    const invoiceMap = useMemo(() => {
        const invoices = matrixData?.invoices || [];
        if (invoices.length === 0) return {};

        // 收集所有 invoice_number（去重后用于计算最小区分长度）
        const uniqueNumbers = [...new Set(
            invoices.map((inv) => inv.invoice_number).filter(Boolean)
        )];

        // 计算最小区分前缀长度：使所有账单号截断后两两不同
        let minLen = 8; // 基线长度
        if (uniqueNumbers.length > 1) {
            for (let len = 1; len <= Math.max(...uniqueNumbers.map((n) => n.length)); len++) {
                const prefixSet = new Set(uniqueNumbers.map((n) => n.slice(0, len)));
                if (prefixSet.size === uniqueNumbers.length) {
                    minLen = len;
                    break;
                }
            }
        }

        // 构建 Map: invoice_data_source_id -> { invoice_number, short_label }
        const map = {};
        invoices.forEach((inv) => {
            const idsId = inv.invoice_data_source_id;
            if (!idsId) return;
            const fullNumber = inv.invoice_number || idsId.slice(0, 8);
            map[idsId] = {
                invoice_number: fullNumber,
                short_label: fullNumber.length > minLen ? fullNumber.slice(0, minLen) : fullNumber,
            };
        });
        return map;
    }, [matrixData?.invoices]);

    // ============================================================
    // 导出
    // ============================================================
    const contextValue = {
        // 基础
        batchNumber,
        ...batchInfo,
        currency: matrixData?.summary?.currency || batchInfo.currency,
        statusSummary,
        pipelineOverview,
        loading: idsLoading,
        error: idsError,

        // IDS 列表
        idsList,

        // 矩阵
        matrixData,
        matrixLoading,
        matrixError,
        eligibleIds,

        // 验证
        validationMap,
        validationSummary,

        // Agent 数据源筛选
        excludeSources,
        toggleSource,

        // 便捷引用
        products: matrixData?.products || {},
        summary: matrixData?.summary || null,
        aircraftGroups: matrixData?.aircraft_groups || [],
        unmatchedCharges: matrixData?.unmatched_charges || [],
        agentSources: matrixData?.agent_sources || [],
        sourceSummary: matrixData?.summary?.source_summary || null,
        dataSources: matrixData?.data_sources || null,
        summaryViewMode: matrixData?.context?.view_mode || null,

        // 账单标识
        invoiceMap,

        // Pipeline 操作
        startPipeline,
        retryFromStage,
        smartRetry,
        batchPipelineAction,
        stopPipeline,
        deleteInvoiceDataSources,
        batchSetSettlementCurrency,
        triggerBatchValidation,
        batchValidationReadiness,
        applyIdsStatusUpdate,

        // 数据刷新
        pollRefresh,     // 静默轮询刷新（无 loading、有 diff 检测）
        refreshBatch,    // 显式刷新 IDS 列表 + 矩阵数据
        refreshMatrix,
        refreshAll,
    };

    return <BatchContext.Provider value={contextValue}>{children}</BatchContext.Provider>;
}

export function useBatchContext() {
    const context = useContext(BatchContext);
    if (!context) {
        throw new Error("useBatchContext must be used within a BatchProvider");
    }
    return context;
}

export default BatchContext;
